// src/pages/ShowDetails.jsx
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { doc, getDoc, collection, getDocs, orderBy, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../auth/AuthContext';
import { BONUS_LIST } from '../components/BonusList';
import { CHAMPIONSHIPS } from '../data/championships';
import ResultsSection from '../components/show/ResultsSection';
import NavigationButtons from '../components/show/NavigationButtons';
import { buildUserFactionWrestlerMap } from '../utils/showHighlights';
import { isTitleMatch as hasMatchTitles } from '../utils/matchTitles';

// Mappa bonus fuori dal componente
const BONUS_MAP = BONUS_LIST.reduce((map, bonus) => {
  map[bonus.value.toLowerCase()] = bonus;
  return map;
}, {});

function ShowDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [show, setShow] = useState(null);
  const [participations, setParticipations] = useState([]);
  const [matches, setMatches] = useState([]);
  const [wrestlerNames, setWrestlerNames] = useState({});
  const [loading, setLoading] = useState(true);
  const [previousEventData, setPreviousEventData] = useState(null);
  const [nextEventData, setNextEventData] = useState(null);
  const [previousEventDataCategory, setPreviousEventDataCategory] = useState(null);
  const [nextEventDataCategory, setNextEventDataCategory] = useState(null);
  const [userFactionWrestlersMap, setUserFactionWrestlersMap] = useState({});
  const [currentTime, setCurrentTime] = useState(() => new Date());
  // Aggiungi stato per forzare il reset della view mode
  const [viewModeReset, setViewModeReset] = useState(0);
  // Vista per show futuri: 'list' (wrestler annunciati) oppure 'match' (griglia match annunciati)
  const [futureViewMode, setFutureViewMode] = useState('list');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const showRef = doc(db, 'shows', id);
        const participationsRef = collection(db, 'shows', id, 'participations');

        const [showSnap, pSnap] = await Promise.all([
          getDoc(showRef),
          getDocs(participationsRef)
        ]);

        if (showSnap.exists()) {
          const data = showSnap.data();
          setShow({
            id: showSnap.id,
            nome: data.nome,
            data: data.data?.toDate() || null,
            type: data.type || 'weekly',
            isExtra: data.isExtra || false,
            logo: data.logo || '',
            sfondo: data.sfondo || '',
          });
        }

        const participationsData = pSnap.docs.map(d => d.data());
        const wrestlerIds = [...new Set(participationsData.map(p => p.wrestlerId))];

        // Fetch wrestlers in batch
        const wrestlerPromises = wrestlerIds.map(async wrestlerId => {
          const wrestlerDoc = await getDoc(doc(db, 'wrestlers', wrestlerId));
          return wrestlerDoc.exists() ? { id: wrestlerDoc.id, ...wrestlerDoc.data() } : null;
        });

        const wrestlers = (await Promise.all(wrestlerPromises)).filter(Boolean);
        const nameMap = {};
        const imageMap = {};
        const genderMap = {};

        wrestlers.forEach(w => {
          nameMap[w.id] = w.name || 'Sconosciuto';
          imageMap[w.id] = w.imageUrl || '/placeholder-wrestler.jpg';
          genderMap[w.id] = w.gender || '';
        });

        setWrestlerNames(nameMap);

        const fullParticipations = participationsData.map(p => ({
          ...p,
          wrestlerName: nameMap[p.wrestlerId],
          wrestlerImage: imageMap[p.wrestlerId],
          gender: genderMap[p.wrestlerId],
        }));

        setParticipations(fullParticipations);

        // Fetch matches
        try {
          const matchesRef = collection(db, 'shows', id, 'matches');
          const matchesQuery = query(matchesRef, orderBy('timestamp', 'asc'));
          const matchesSnap = await getDocs(matchesQuery);
          const matchesData = matchesSnap.docs.map(d => ({ id: d.id, ...d.data() }));
          setMatches(matchesData);
        } catch (err) {
          console.warn('No matches found:', err);
          setMatches([]);
        }
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    console.log('Show Details loaded:', show); // Debug
  }, [id]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setCurrentTime(new Date());
    }, 30000);

    return () => window.clearInterval(intervalId);
  }, []);

  // Reset della view mode quando cambia lo show
  useEffect(() => {
    setViewModeReset(prev => prev + 1);
    setFutureViewMode('list');
  }, [id]);

  useEffect(() => {
    const fetchAdjacentEvents = async () => {
      try {
        const showsSnap = await getDocs(query(collection(db, 'shows'), orderBy('data', 'asc')));
        const shows = showsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        const currentIdx = shows.findIndex(s => s.id === id);

        // Navigazione cronologica
        setPreviousEventData(currentIdx > 0 ? shows[currentIdx - 1] : null);
        setNextEventData(currentIdx < shows.length - 1 ? shows[currentIdx + 1] : null);

        // Navigazione per categoria
        if (show?.type) {
          let categoryShows;

          if (show.type === 'ple') {
            // Per PLE: filtra per type === 'ple'
            categoryShows = shows.filter(s => s.type === 'ple');
          } else {
            // Per weekly: filtra per nome dello show
            categoryShows = shows.filter(s => s.nome === show.nome);
          }

          const categoryIdx = categoryShows.findIndex(s => s.id === id);
          setPreviousEventDataCategory(categoryIdx > 0 ? categoryShows[categoryIdx - 1] : null);
          setNextEventDataCategory(categoryIdx < categoryShows.length - 1 ? categoryShows[categoryIdx + 1] : null);
        }
      } catch (err) {
        setPreviousEventData(null);
        setNextEventData(null);
        setPreviousEventDataCategory(null);
        setNextEventDataCategory(null);
      }
    };

    fetchAdjacentEvents();
  }, [id, show?.type, show?.nome]);

  useEffect(() => {
    const fetchUserFactionWrestlers = async () => {
      if (!currentUser) {
        setUserFactionWrestlersMap({});
        return;
      }

      try {
        const userSnap = await getDoc(doc(db, 'users', currentUser.uid));
        const userData = userSnap.exists() ? userSnap.data() : null;
        const preferredFactionIds = [
          ...(Array.isArray(userData?.factionIds) ? userData.factionIds : []),
          userData?.factionId,
          userData?.favoriteFactionId,
        ]
          .filter(Boolean)
          .filter((factionId, index, factionIds) => factionIds.indexOf(factionId) === index);

        let factionDocs = [];

        if (preferredFactionIds.length > 0) {
          factionDocs = await Promise.all(
            preferredFactionIds.map((factionId) => getDoc(doc(db, 'factions', factionId)))
          );
        } else {
          const factionsSnap = await getDocs(
            query(collection(db, 'factions'), where('ownerUid', '==', currentUser.uid))
          );
          factionDocs = factionsSnap.docs;
        }

        const userFactions = factionDocs
          .filter((factionDoc) => factionDoc.exists())
          .map((factionDoc) => ({ id: factionDoc.id, ...factionDoc.data() }));

        setUserFactionWrestlersMap(buildUserFactionWrestlerMap(userFactions));
      } catch (error) {
        console.error('Errore nel caricamento della fazione utente:', error);
        setUserFactionWrestlersMap({});
      }
    };

    fetchUserFactionWrestlers();
  }, [currentUser]);

  // Funzione per ottenere l'icona del titolo
  const getTitleIcon = (titleValue) => {
    const championship = CHAMPIONSHIPS.find(ch => ch.value === titleValue);
    return championship?.icon || '🏆';
  };

  // Funzione per determinare se mostrare l'icona del titolo sul perdente
  const shouldShowTitleOnLoser = (match, wrestlerId) => {
    if (!hasMatchTitles(match) || !match.winner) return false;

    // Se è una vittoria per squalifica e il wrestler non è il vincitore
    if (match.notes?.includes('Vittoria per squalifica')) {
      return wrestlerId !== match.winner;
    }

    return false;
  };

  // Stile corretto per l'header - senza mix di proprietà shorthand e non-shorthand
  const headerStyle = {
    backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.42)), url(${show?.sfondo || ''})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    minHeight: '300px',
  };

  const sortedParticipations = [...participations].sort((a, b) => a.wrestlerName.localeCompare(b.wrestlerName));
  const highlightedParticipations = sortedParticipations
    .filter((participation) => userFactionWrestlersMap[participation.wrestlerId])
    .map((participation) => ({
      ...participation,
      userFactionMeta: userFactionWrestlersMap[participation.wrestlerId],
    }))
    .sort((a, b) => {
      const countDifference = (b.userFactionMeta?.count || 0) - (a.userFactionMeta?.count || 0);
      if (countDifference !== 0) return countDifference;
      return a.wrestlerName.localeCompare(b.wrestlerName);
    });
  const regularParticipations = sortedParticipations.filter(
    (participation) => !userFactionWrestlersMap[participation.wrestlerId]
  );

  if (loading) {
    return (
      <div className="container py-5 text-center">
        <div className="spinner-border text-muted" role="status">
          <span className="visually-hidden">Caricamento...</span>
        </div>
      </div>
    );
  }

  if (!show) {
    return (
      <div className="container py-5 text-center">
        <h3>Show non trovato</h3>
        <button className="btn mt-3" style={{
          backgroundColor: 'var(--brand-primary)',
          color: 'white',
          border: '2px solid var(--brand-primary)'
        }} onClick={() => navigate('/shows')}>
          Torna agli Show
        </button>
      </div>
    );
  }

  const isFutureShow = show.data && new Date(show.data) > currentTime;

  return (
    <div className="show-page position-relative">
      {/* Header con stile corretto */}
      <div
        className="show-page__header position-relative object-fit-cover"
        style={headerStyle}
      >
        <div className="show-page__fade-bottom" />
      </div>

      <div className="show-page__content container position-relative">
        {/* Logo e info show */}
        <div className="text-center mb-4">
          {show.logo && (
            <img
              src={show.logo}
              alt={show.nome}
              className="show-logo mb-3 img-fluid"
              style={{ maxHeight: '100px' }}
            />
          )}
          <p className="text-light mb-0">
            {show.data?.toLocaleDateString('it-IT', {
              weekday: 'long',
              day: 'numeric',
              month: 'long',
              year: 'numeric',
            })}
          </p>
        </div>

        {/* Sezione per show futuri */}
        {isFutureShow && futureViewMode === 'list' && (
          <div
            className="card shadow-lg border-0 rounded-4 overflow-hidden mb-4"
            style={{
              background: 'linear-gradient(180deg, rgba(18, 24, 31, 0.98), rgba(28, 38, 49, 0.96))',
              border: '1px solid rgba(146, 201, 252, 0.12)'
            }}
          >
            <div className="card-header py-3" style={{
              background: 'linear-gradient(135deg, var(--brand-primary), var(--brand-primary-light))',
              color: 'white',
              borderTop: '4px solid var(--brand-secondary)'
            }}>
              <div className="d-flex justify-content-between align-items-center">
                <h6 className="mb-0 fs-6">
                  <i className="bi bi-people-fill me-2"></i> Wrestler annunciati
                </h6>
                {matches.length > 0 && (
                  <div className="btn-group btn-group-sm results-view-toggle" role="group" aria-label="View mode">
                    <button
                      type="button"
                      className={`btn ${futureViewMode === 'list' ? 'results-view-btn-active' : 'results-view-btn'}`}
                      onClick={() => setFutureViewMode('list')}
                      title="Lista"
                    >
                      <i className="bi bi-people-fill"></i>
                    </button>
                    <button
                      type="button"
                      className={`btn ${futureViewMode === 'match' ? 'results-view-btn-active' : 'results-view-btn'}`}
                      onClick={() => setFutureViewMode('match')}
                      title="Match"
                    >
                      <i className="bi bi-diagram-3"></i>
                    </button>
                  </div>
                )}
              </div>
            </div>
            <div className="card-body" style={{ background: 'transparent' }}>
              {participations.length === 0 ? (
                <div className="text-center py-3">
                  <i className="bi bi-people text-muted fs-4"></i>
                  <p className="text-muted mb-0">Nessun wrestler confermato</p>
                </div>
              ) : (
                <>
                  {currentUser && highlightedParticipations.length > 0 && (
                    <div className="mb-4">
                      <div
                        className="text-center main-faction mb-3 fw-bold rounded-pill px-3 py-2"
                        style={{
                          cursor: 'default',
                          background: 'rgba(194, 157, 84, 0.14)',
                          border: '1px solid rgba(194, 157, 84, 0.3)',
                          color: '#f1d39a',
                          letterSpacing: '0.03em'
                        }}
                      >
                        Wrestler delle tue fazioni
                      </div>
                      <div className="row g-3">
                        {highlightedParticipations.map((p, idx) => (
                          <div key={`highlighted-${idx}`} className="col-6 col-sm-4 col-md-3 col-lg-2">
                            <div
                              className="wrestler-card shadow-sm position-relative overflow-hidden rounded-4 cursor-pointer"
                              style={{
                                background: 'rgba(255,255,255,0.06)',
                                border: '1px solid rgba(194, 157, 84, 0.38)',
                                boxShadow: '0 10px 24px rgba(0, 0, 0, 0.28)'
                              }}
                              onClick={() => navigate(`/wrestlers/${p.wrestlerId}`)}
                            >
                              <div className="position-relative">
                                <img
                                  src={p.wrestlerImage}
                                  alt={p.wrestlerName}
                                  className="img-fluid w-100 bg-dark"
                                  style={{
                                    height: '160px',
                                    objectFit: 'cover',
                                    objectPosition: 'top center'
                                  }}
                                  onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = '/placeholder-wrestler.jpg';
                                  }}
                                />
                                {p.title && (
                                  <div className="position-absolute top-0 start-0 m-1">
                                    <span
                                      className="badge p-1"
                                      style={{
                                        backgroundColor: 'var(--brand-secondary)',
                                        color: 'white'
                                      }}
                                      title={CHAMPIONSHIPS.find(ch => ch.value === p.title)?.name || 'Titolo'}
                                    >
                                      {getTitleIcon(p.title)}
                                    </span>
                                  </div>
                                )}
                                {p.userFactionMeta?.count > 0 && (
                                  <div className="position-absolute top-0 end-0 m-1">
                                    <span
                                      className="badge rounded-pill px-2 py-1"
                                      style={{
                                        backgroundColor: 'rgba(18, 24, 31, 0.9)',
                                        color: '#f1d39a',
                                        border: '1px solid rgba(194, 157, 84, 0.4)',
                                      }}
                                      title={p.userFactionMeta.factionNames.join(', ')}
                                    >
                                      {p.userFactionMeta.count === 1
                                        ? ''
                                        : `${p.userFactionMeta.count}`}
                                    </span>
                                  </div>
                                )}
                                <div
                                  className="position-absolute bottom-0 start-0 end-0 p-2 name-overlay"
                                  style={{
                                    background: 'linear-gradient(to top, rgba(18, 24, 31), rgba(0, 0, 0, 0))'
                                  }}
                                >
                                  <h6 className="text-white text-truncate mb-0 fw-semibold">
                                    {p.wrestlerName}
                                  </h6>
                                  <small className="d-block text-truncate" style={{ color: '#f1d39a' }}>
                                    {p.userFactionMeta?.factionNames.join(', ')}
                                  </small>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                      {regularParticipations.length > 0 && (
                        <div className="my-4">
                          <hr style={{ borderColor: 'rgba(146, 201, 252, 0.2)' }} className="opacity-75 mb-2" />
                          <div className="small text-center text-brand-secondary">Altri wrestler annunciati</div>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="row g-3">
                    {(currentUser && highlightedParticipations.length > 0 ? regularParticipations : sortedParticipations).map((p, idx) => (
                      <div key={idx} className="col-6 col-sm-4 col-md-3 col-lg-2">
                        <div
                          className="wrestler-card shadow-sm position-relative overflow-hidden rounded-4 cursor-pointer"
                          style={{
                            background: 'rgba(255,255,255,0.04)',
                            border: '1px solid rgba(146, 201, 252, 0.14)',
                            boxShadow: '0 10px 22px rgba(0, 0, 0, 0.24)'
                          }}
                          onClick={() => navigate(`/wrestlers/${p.wrestlerId}`)}
                        >
                          <div className="position-relative">
                            <img
                              src={p.wrestlerImage}
                              alt={p.wrestlerName}
                              className="img-fluid w-100 bg-dark"
                              style={{
                                height: '160px',
                                objectFit: 'cover',
                                objectPosition: 'top center'
                              }}
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = '/placeholder-wrestler.jpg';
                              }}
                            />
                            {/* Icona titolo se presente */}
                            {p.title && (
                              <div className="position-absolute top-0 start-0 m-1">
                                <span
                                  className="badge p-1"
                                  style={{
                                    backgroundColor: 'var(--brand-secondary)',
                                    color: 'white'
                                  }}
                                  title={CHAMPIONSHIPS.find(ch => ch.value === p.title)?.name || 'Titolo'}
                                >
                                  {getTitleIcon(p.title)}
                                </span>
                              </div>
                            )}
                            <div
                              className="position-absolute bottom-0 start-0 end-0 p-2 name-overlay"
                              style={{
                                background: 'linear-gradient(to top, rgba(18, 24, 31), rgba(0, 0, 0, 0))'
                              }}
                            >
                              <h6 className="text-white text-truncate mb-0 fw-semibold">
                                {p.wrestlerName}
                              </h6>
                            </div>
                          </div>

                          {/* Note sotto l'immagine */}
                          {p.notes && (
                            <div className="p-2 border-top" style={{
                              background: 'rgba(194, 157, 84, 0.08)',
                              borderTopColor: 'rgba(194, 157, 84, 0.28)'
                            }}>
                              <small className="d-block text-center" style={{ color: 'var(--brand-secondary)' }}>
                                <i className="bi bi-chat-text me-1"></i>
                                {p.notes}
                              </small>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Griglia match annunciati per show futuri */}
        {isFutureShow && futureViewMode === 'match' && matches.length > 0 && (
          <ResultsSection
            mode="announced"
            participations={participations}
            matches={matches}
            wrestlerNames={wrestlerNames}
            bonusMap={BONUS_MAP}
            championships={CHAMPIONSHIPS}
            getTitleIcon={getTitleIcon}
            shouldShowTitleOnLoser={shouldShowTitleOnLoser}
            showData={show}
            futureViewMode={futureViewMode}
            onFutureViewModeChange={setFutureViewMode}
            userFactionWrestlersMap={userFactionWrestlersMap}
            key={`announced-${viewModeReset}`}
          />
        )}

        {/* Risultati per show passati */}
        {!isFutureShow && (
          <ResultsSection
            participations={participations}
            matches={matches}
            wrestlerNames={wrestlerNames}
            bonusMap={BONUS_MAP}
            championships={CHAMPIONSHIPS}
            // Passa le funzioni helper per la gestione dei titoli
            getTitleIcon={getTitleIcon}
            shouldShowTitleOnLoser={shouldShowTitleOnLoser}
            showData={show}
            // Passa la key per forzare il reset
            key={`results-${viewModeReset}`}
          />
        )}
      </div>

      {/* Navigazione */}
      <NavigationButtons
        previousEventData={previousEventData}
        nextEventData={nextEventData}
        previousEventDataCategory={previousEventDataCategory}
        nextEventDataCategory={nextEventDataCategory}
        navigate={navigate}
        currentEvent={show}
      />

      {/* Bottone torna indietro */}
      <div className="mt-4 text-center">
        <div className="justify-content-end">
          <Link to="/shows" className="btn-back mb-4">
            <i className="bi bi-arrow-right me-2" />
            Torna agli Show
          </Link>
        </div>
      </div>
    </div>
  );
}

export default ShowDetails;
